import type { DataSchema, Schema } from 'types';
import { useTranslation } from 'react-i18next';
import { memo, useState } from 'react';
import { Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import { MoreVert } from '@mui/icons-material';
import { Button } from 'components/mui/common/Button';
import { Checkbox } from 'components/mui/common/Checkbox';
import type { Header } from '@tanstack/react-table';
import { TABLE_CELL_TYPE } from 'types/enums.ts';
import { AGGREGATION_OPTIONS, NUMERIC_HELIOS_DATA_TYPES, type TableAggregationType } from 'utils/table';

type ColumnMeta<P extends DataSchema> = {
  accessorKey?: string;
  processedSchema?: P;
};

const PinButtonBase = <T extends keyof P, P extends DataSchema>({
  header,
  aggregation = null,
  onAggregationChange,
  onColumnSizingReset,
  isColumnWidthChanged = false,
  isColumnSizingResetEnabled = false,
  isEnableColumnAggregation = true,
}: {
  header: Header<Schema<T, P>, unknown>;
  aggregation?: TableAggregationType | null;
  onAggregationChange?: (aggregation: TableAggregationType | null) => void;
  onColumnSizingReset?: () => void;
  isColumnWidthChanged?: boolean;
  isColumnSizingResetEnabled?: boolean;
  isEnableColumnAggregation?: boolean;
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isPinnedLeft = header.column.getIsPinned() === 'left';
  const isPinnedRight = header.column.getIsPinned() === 'right';
  const columnMeta = header.column.columnDef.meta as ColumnMeta<P> | undefined;
  const schemaItem = columnMeta?.accessorKey ? columnMeta.processedSchema?.[columnMeta.accessorKey] : undefined;
  const isNumericColumn = Boolean(
    schemaItem &&
      (schemaItem.type === TABLE_CELL_TYPE.NUMBER ||
        schemaItem.originalType === 'Number' ||
        (schemaItem.type === TABLE_CELL_TYPE.HELIOS_VALUE && NUMERIC_HELIOS_DATA_TYPES.has(schemaItem.dataType ?? '')))
  );
  const aggregationOptions = AGGREGATION_OPTIONS.filter((option) => {
    if (option.value === 'sum' || option.value === 'avg') return isNumericColumn;

    return true;
  });

  return (
    <>
      <Button onClick={handleClick} variant="text" size="small" type="button" sx={{ padding: 0, minWidth: 'unset' }}>
        <MoreVert />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ marginTop: '4px' }}
        MenuListProps={{ sx: { marginY: '4px', maxHeight: '640px', overflow: 'auto', textOverflow: 'ellipsis' } }}>
        <MenuItem
          key={`${header.id}-left`}
          onClick={() => {
            header.column.pin(isPinnedLeft ? false : 'left');
            handleClose();
          }}>
          <Checkbox checked={isPinnedLeft} />
          <ListItemText primary={t('common.pinLeft')} />
        </MenuItem>
        <MenuItem
          key={`${header.id}-right`}
          onClick={() => {
            header.column.pin(isPinnedRight ? false : 'right');
            handleClose();
          }}>
          <Checkbox checked={isPinnedRight} />
          <ListItemText primary={t('common.pinRight')} />
        </MenuItem>
        {isColumnSizingResetEnabled && isColumnWidthChanged && (
          <>
            <Divider />
            <MenuItem
              key={`${header.id}-reset-sizing`}
              onClick={() => {
                onColumnSizingReset?.();
                handleClose();
              }}>
              <ListItemText primary={t('common.resetColumnWidth')} />
            </MenuItem>
          </>
        )}
        {isEnableColumnAggregation && (
          <>
            <Divider />
            <MenuItem key={`${header.id}-aggregation-label`} disabled>
              <ListItemText primary={t('common.aggregation')} />
            </MenuItem>
            {aggregationOptions.map((option) => (
              <MenuItem
                key={`${header.id}-aggregation-${option.value ?? 'none'}`}
                onClick={() => {
                  onAggregationChange?.(option.value);
                  handleClose();
                }}>
                <Checkbox checked={aggregation === option.value} />
                <ListItemText primary={t(option.labelKey)} />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};

export const PinButton = memo(PinButtonBase) as unknown as typeof PinButtonBase;
